import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fetchTrending } from "../lib/api";
import CASE_FILE_MANIFEST, { safeRectAsPadding } from "../components/spotlight/caseFileManifest";

const _TEX = "/textures";
const _CASE_COUNT = 10;

/* -----------------------------------------------------------
 * Deterministic pickers — same slug always maps to the same
 * folder photograph and the same on-desk placement.
 * --------------------------------------------------------- */
const _stringHash = (s) => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
};

const _mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const _caseVar = (seed) => {
  const n = _stringHash(String(seed || ""));
  return String((n % _CASE_COUNT) + 1).padStart(2, "0");
};

/* -----------------------------------------------------------
 * Authentic archival metadata generators. Every generator is
 * DETERMINISTIC from the topic slug so a dossier's paperwork
 * never changes between renders — the same file always has
 * the same case number and REF code.
 *
 * We keep the vocabulary sparse and true to the reference:
 * CATEGORY  ←—  topic.category
 * REF       ←—  initials of the title, joined by dots +
 *               a two-digit seq. e.g. "K.V-01", "R.B.D-77",
 *               "O.M-43". Feels like a real archivist's
 *               internal filing code.
 * C: NN     ←—  controversy score
 * E: NN     ←—  evidence score
 * --------------------------------------------------------- */
const _archiveMeta = (topic) => {
  const slug = topic.slug || topic.id || "";
  const h = _stringHash(slug);
  const title = (topic.title || "").trim();
  // First letter of every capitalised word — filed initials.
  const parts = title
    .split(/\s+/)
    .filter((w) => /^[A-Z0-9]/.test(w))
    .slice(0, 3)
    .map((w) => w[0].toUpperCase());
  const initials = parts.length ? parts.join(".") : "X.X";
  const seq = String(((h >>> 8) % 90) + 10);
  return {
    ref: `${initials}-${seq}`,
    controversy: topic.controversy_score ?? 0,
    evidence: topic.evidence_score ?? "—",
  };
};

const _caseFileBg = (seed) => {
  const variant = _caseVar(seed);
  const key = `case-file-${variant}`;
  const url = `${_TEX}/${key}.png`;
  const meta = CASE_FILE_MANIFEST[key];
  const aspect = meta?.aspect ?? 1.78;
  const pad = safeRectAsPadding(key);
  // Two-layer vignette clipped by the paper's own alpha mask.
  // The outer stops are strong enough to fully suppress the
  // studio-lit bright rim baked into the paper photographs so
  // the folder blends into the dark wood without a cutout
  // outline. The gradient is stacked BEHIND nothing and OVER
  // the paper image, both clipped by the same mask, so the
  // darkening follows the true paper silhouette (torn edges,
  // folded corners, paperclip cutouts, punched holes) — no
  // per-variant tuning and no destructive alpha work.
  const vignette =
    "radial-gradient(ellipse 96% 96% at 50% 50%, " +
      "rgba(0,0,0,0) 62%, " +
      "rgba(4,3,1,0.55) 88%, " +
      "rgba(4,3,1,0.92) 100%)";
  return {
    backgroundImage: `${vignette}, url("${url}")`,
    backgroundSize: "100% 100%, 100% 100%",
    backgroundPosition: "center center, center center",
    backgroundRepeat: "no-repeat, no-repeat",
    backgroundColor: "transparent",
    maskImage: `url("${url}")`,
    maskSize: "100% 100%",
    maskRepeat: "no-repeat",
    maskPosition: "center center",
    WebkitMaskImage: `url("${url}")`,
    WebkitMaskSize: "100% 100%",
    WebkitMaskRepeat: "no-repeat",
    WebkitMaskPosition: "center center",
    "--paper-aspect": String(aspect),
    paddingTop: pad.paddingTop,
    paddingRight: pad.paddingRight,
    paddingBottom: pad.paddingBottom,
    paddingLeft: pad.paddingLeft,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: "0.6em",
  };
};

/* -----------------------------------------------------------
 * DossierCover — the shared typewritten composition rendered
 * inside a case-file folder. Same typewriter face at three
 * type scales, generous breathing room, a short filing rule
 * before the footer. Modelled on real archival covers, not
 * modern UI cards. `variant` controls the type scale.
 * --------------------------------------------------------- */
const DossierCover = ({ topic, variant = "secondary" }) => {
  const meta = _archiveMeta(topic);
  const category = (topic.category || "UNCLASSIFIED").toUpperCase();
  return (
    <div className={`dossier-cover dossier-cover--${variant}`}>
      {/* Filing header — two typewritten lines, stacked.
          Line 1: subject category. Line 2: archivist REF. */}
      <header className="dossier-cover__filing" data-testid="dossier-filing">
        <div className="dossier-cover__class">{category}</div>
        <div className="dossier-cover__ref">REF: {meta.ref}</div>
      </header>

      {/* Subject block — the actual cover copy of the file.
          Title in a typewriter face, then the archivist's
          brief in the SAME face at a smaller size. Real
          dossiers do not mix serif and mono; they type
          everything on one machine. */}
      <div className="dossier-cover__subject">
        <h3 className="dossier-cover__title" data-testid="dossier-title">
          {topic.title}
        </h3>
        {topic.tagline && (
          <p className="dossier-cover__brief">
            {topic.tagline}
          </p>
        )}
      </div>

      {/* Filing rule + typewritten scores. The rule is a
          short pen-stroke sitting above the footer, echoing
          the underline a records clerk draws before writing
          the final metadata line. */}
      <footer className="dossier-cover__metadata">
        <span className="dossier-cover__rule" aria-hidden="true" />
        <div className="dossier-cover__stats">
          <span>C:&nbsp;{meta.controversy}</span>
          <span className="dossier-cover__gap" aria-hidden="true" />
          <span>E:&nbsp;{meta.evidence}</span>
        </div>
      </footer>
    </div>
  );
};

/** Featured dossier — larger folder centered under the lamp. */
const FeaturedFile = ({ topic }) => {
  const id = topic.slug || topic.id;
  return (
    <Link
      to={`/topic/${id}`}
      data-testid="spotlight-featured"
      className="manila-card dossier-featured block max-w-3xl mx-auto"
    >
      <div className="paper" style={_caseFileBg(id)}>
        <DossierCover topic={topic} variant="featured" />
      </div>
    </Link>
  );
};

/** Secondary files — tilted manila cards laid across the desk. */
const TableFile = ({ topic }) => {
  const id = topic.slug || topic.id;
  const seed = _stringHash(id);
  const rand = _mulberry32(seed);
  const rot = ((rand() - 0.5) * 8).toFixed(2);
  const dx = ((rand() - 0.5) * 140).toFixed(0);
  const dy = ((rand() - 0.5) * 30).toFixed(0);
  const style = { transform: `translate(${dx}px, ${dy}px) rotate(${rot}deg)` };
  return (
    <div className="dossier-secondary">
      <Link
        to={`/topic/${id}`}
        data-testid={`spotlight-file-${id}`}
        className="manila-card block max-w-3xl mx-auto"
        style={style}
      >
        <div className="paper" style={_caseFileBg(id)}>
          <DossierCover topic={topic} variant="secondary" />
        </div>
      </Link>
    </div>
  );
};

const Spotlight = () => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrending(7)
      .then(setTopics)
      .catch(() => setTopics([]))
      .finally(() => setLoading(false));
  }, []);

  const featured = topics[0];
  const rest = topics.slice(1);

  return (
    <div
      className="spotlight-vnext"
      data-testid="spotlight-page"
      style={{
        "--hero-bg": `url("${_TEX}/investigation-desk-hero.jpg")`,
      }}
    >
      {/* ---- The Investigation Desk ---- */}
      <div className="scene" aria-hidden="true" />
      <div className="scene-vignette" aria-hidden="true" />

      {/* ---- Forward-facing intro (title / subtitle / status pill) ---- */}
      <div className="forward-ui" data-testid="spotlight-forward-ui">
        <div className="eyebrow">// SPOTLIGHT</div>
        <h1>Files currently under examination</h1>
        <p>
          The dossiers drawing the most heat this week — most-viewed,
          most-voted, most-debated. The lamp is on. The desk is warm.
        </p>
        <div className="status-pill" data-testid="spotlight-status">
          <span className="dot" />
          LAMP ON // ARCHIVE ACTIVE
        </div>
      </div>

      {/* ---- Dossiers laid out on the desk ---- */}
      <div className="dossier-stream">
        {loading ? (
          <p className="text-center font-mono text-xs uppercase tracking-widest text-[#8a7550] mt-12">
            Retrieving files…
          </p>
        ) : !featured ? (
          <p className="text-center font-mono text-xs uppercase tracking-widest text-[#8a7550]">
            No files on the desk yet.
          </p>
        ) : (
          <FeaturedFile topic={featured} />
        )}

        <div className="mt-24 space-y-14">
          {rest.map((t) => (
            <TableFile key={t.id} topic={t} />
          ))}
        </div>

        {/* Engraved CTA — carved into the desk, not a button */}
        <div className="mt-24 mb-16 text-center">
          <Link
            to="/archive"
            data-testid="spotlight-archive-link"
            className="engraved-cta group inline-flex items-center gap-4 px-10 py-4 font-mono text-sm md:text-base uppercase tracking-[0.32em]"
          >
            Browse the full archive
            <ArrowRight size={16} className="engraved-arrow" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Spotlight;
